import { useQuery } from '@tanstack/react-query';
import { getWorkspacesApi } from './api';
import type {
  LLMConfig,
  CreateLLMConfigRequest,
  UpdateLLMConfigRequest,
  ListLLMConfigsParams
} from '../types/llm-config';

export type LLMConfigApi = ReturnType<typeof useLLMConfigApi>;

export const llmConfigKeys = {
  all: ['llm-configs'] as const,
  list: (params: ListLLMConfigsParams = {}) => [...llmConfigKeys.all, params] as const,
};

/** Shared query for `/admin/llm-configs`. */
export function useLLMConfigs(params: ListLLMConfigsParams = {}) {
  const llmApi = useLLMConfigApi();
  return useQuery<LLMConfig[]>({
    queryKey: llmConfigKeys.list(params),
    queryFn: () => llmApi.listLLMConfigs(params),
    staleTime: 5 * 60_000,
  });
}

/**
 * Hook-based API for LLM configuration management
 */
export const useLLMConfigApi = () => {
  const apiInstance = getWorkspacesApi();

  return {
    listLLMConfigs: async (params?: ListLLMConfigsParams): Promise<LLMConfig[]> => {
      console.log('Fetching LLM configurations', params);
      const queryParams = new URLSearchParams();
      if (params?.provider) {
        queryParams.append('provider', params.provider);
      }
      if (params?.activeOnly !== undefined) {
        queryParams.append('active_only', String(params.activeOnly));
      }
      const url = `/admin/llm-configs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiInstance.get<LLMConfig[]>(url);
      return response.data;
    },

    getLLMConfig: async (modelName: string): Promise<LLMConfig> => {
      console.log('Fetching LLM config:', modelName);
      const response = await apiInstance.get<LLMConfig>(`/admin/llm-configs/${encodeURIComponent(modelName)}`);
      return response.data;
    },

    createLLMConfig: async (data: CreateLLMConfigRequest): Promise<LLMConfig> => {
      console.log('Creating LLM config:', data.modelId);
      const response = await apiInstance.post<LLMConfig>('/admin/llm-configs', data);
      return response.data;
    },

    updateLLMConfig: async (modelName: string, data: UpdateLLMConfigRequest): Promise<LLMConfig> => {
      console.log('Updating LLM config:', modelName, data);
      const response = await apiInstance.put<LLMConfig>(`/admin/llm-configs/${encodeURIComponent(modelName)}`, data);
      return response.data;
    },

    deleteLLMConfig: async (modelName: string): Promise<void> => {
      console.log('Deleting LLM config:', modelName);
      await apiInstance.delete(`/admin/llm-configs/${encodeURIComponent(modelName)}`);
    },

    exportToCSV: async (): Promise<Blob> => {
      console.log('Exporting LLM configs to CSV');
      const response = await apiInstance.get('/admin/llm-configs/export/csv', {
        responseType: 'blob'
      });
      return response.data;
    },

    importFromCSV: async (file: File, mergeExisting: boolean = false): Promise<{
      status: string;
      results: {
        created: number;
        updated: number;
        skipped: number;
        failed: number;
      };
      message: string;
    }> => {
      console.log('Importing LLM configs from CSV', { mergeExisting });
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiInstance.post(
        `/admin/llm-configs/import/csv?merge_existing=${mergeExisting}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
  };
};

